/**
 * @class AccountSwitcher
 * @extends HTMLElement
 */
class AccountSwitcher extends HTMLElement {
  constructor() {
    super();

    // elements
    this.handlers = this.querySelectorAll('[data-state-handler]');
    this.coordinates = {
      distance: '20px',
      initX: this.offsetLeft,
      initY: this.offsetTop,
      initRight: window.innerWidth - this.getBoundingClientRect().right,
      initBottom: window.innerHeight - this.getBoundingClientRect().bottom,
    };

    // settings
    this.eventSettings = {
      time: 0,
      interval: null,
      threshold: 3,
    };

    // event listeners
    this.handlers.forEach((handler) => {
      // mouse events
      handler.addEventListener('mousedown', (e) => this.handleStart(e, handler));
      handler.addEventListener('mouseup', () => this.handleEnd(handler));
    });

    // init
    this.getPosition();

    // Add resize observer
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(document.body);
  }

  /**
   * @method handleStart
   * @description Handles the start of an event
   * @param {MouseEvent} e
   * @param {HTMLElement} handler
   */
  handleStart(e, handler) {
    // start counting the time when mouseDown/touchstart
    this.eventSettings.interval = setInterval(() => {
      this.eventSettings.time++;
    }, 100);
  }

  /**
   * @method handleEnd
   * @param {HTMLElement} handler
   */
  handleEnd(handler) {
    this.handleState(handler.dataset.stateHandler);

    // event is handled, clear the interval and reset the time
    clearInterval(this.eventSettings.interval);
    this.eventSettings.time = 0;
  }

  /**
   * @method updatePosition
   * @description Updates the position of the element
   * @param {MouseEvent} e
   * @param {HTMLElement} handler
   */
  updatePosition(e, handler) {
    e.preventDefault();

    // Get coordinates from either mouse or touch event
    const eventCoordinates = e.type.includes('touch') ? e.touches[0] : e;

    // Store the current position of the element
    this.coordinates.initX = this.offsetLeft;
    this.coordinates.initY = this.offsetTop;
    this.coordinates.initRight = window.innerWidth - this.getBoundingClientRect().right;
    this.coordinates.initBottom = window.innerHeight - this.getBoundingClientRect().bottom;

    // Store the initial mouse/touch position
    this.coordinates.firstX = eventCoordinates.pageX;
    this.coordinates.firstY = eventCoordinates.pageY;
  }

  /**
   * @method handleState
   * @description Layout behavior on click
   * @param {string} type
   */
  handleState(type) {
    switch (type) {
      case 'top-left':
        this.style.left = this.coordinates.distance;
        this.style.top = this.coordinates.distance;
        this.style.right = '';
        this.style.bottom = '';
        this.savePosition();
        break;
      case 'top-right':
        this.style.left = '';
        this.style.top = this.coordinates.distance;
        this.style.right = this.coordinates.distance;
        this.style.bottom = '';
        this.savePosition();
        break;
      case 'bottom-left':
        this.style.left = this.coordinates.distance;
        this.style.top = '';
        this.style.right = '';
        this.style.bottom = this.coordinates.distance;
        this.savePosition();
        break;
      case 'bottom-right':
        this.style.left = '';
        this.style.top = '';
        this.style.right = this.coordinates.distance;
        this.style.bottom = this.coordinates.distance;
        this.savePosition();
        break;
      case 'close':
        this.classList.remove('ids-is-open');
        localStorage.setItem('ids-is-open', 'false');
        break;
      case 'open':
        this.classList.add('ids-is-open');
        localStorage.setItem('ids-is-open', 'true');
        break;
    }
  }

  /**
   * @method savePosition
   * @description Saves the position of the element
   */
  savePosition() {
    const dragObject = document.querySelector(this.localName);

    localStorage.setItem('ids-position-left', dragObject.style.left);
    localStorage.setItem('ids-position-top', dragObject.style.top);
    localStorage.setItem('ids-position-right', dragObject.style.right);
    localStorage.setItem('ids-position-bottom', dragObject.style.bottom);
  }

  /**
   * @method getPosition
   * @description Gets the position of the element
   */
  getPosition() {
    const dragObject = document.querySelector(this.localName);

    // Calculate maximum valid positions based on viewport and element size
    const vWidth = window.innerWidth - dragObject.offsetWidth;
    const vHeight = window.innerHeight - dragObject.offsetHeight;

    // Get saved positions from localStorage
    const top = localStorage.getItem('ids-position-top');
    const right = localStorage.getItem('ids-position-right');
    const bottom = localStorage.getItem('ids-position-bottom');
    const left = localStorage.getItem('ids-position-left');

    // Check if saved positions would place element outside viewport
    const isTooTop = top !== '' && parseInt(top, 10) > vHeight;
    const isTooRight = right !== '' && parseInt(right, 10) > vWidth;
    const isTooBottom = bottom !== '' && parseInt(bottom, 10) > vHeight;
    const isTooLeft = left !== '' && parseInt(left, 10) > vWidth;

    // If we have valid saved positions, use them
    if (left || top || right || bottom) {
      dragObject.style.left = isTooLeft ? vWidth + 'px' : left;
      dragObject.style.top = isTooTop ? vHeight + 'px' : top;
      dragObject.style.right = isTooRight ? vWidth + 'px' : right;
      dragObject.style.bottom = isTooBottom ? vHeight + 'px' : bottom;
    } else {
      // Default position if no saved position exists
      dragObject.style.left = '';
      dragObject.style.top = '';
      dragObject.style.right = this.coordinates.distance;
      dragObject.style.bottom = this.coordinates.distance;
    }

    // Set open state separately from position
    if (localStorage.getItem('ids-is-open') === 'true') {
      this.classList.add('ids-is-open');
    }
  }

  /**
   * @method handleResize
   * @description Handles the resize of the element
   */
  handleResize() {
    const vWidth = window.innerWidth - this.offsetWidth;
    const vHeight = window.innerHeight - this.offsetHeight;

    // Get current position
    const left = parseInt(this.style.left);
    const top = parseInt(this.style.top);

    // Adjust if needed
    if (!isNaN(left)) {
      this.style.left = `${Math.min(left, vWidth)}px`;
    }
    if (!isNaN(top)) {
      this.style.top = `${Math.min(top, vHeight)}px`;
    }
  }

  /**
   * @method disconnectedCallback
   * @description Disconnects the resize observer
   */
  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.AccountSwitcher = AccountSwitcher;
  customElements.define('account-switcher', AccountSwitcher);
});
